// ═══════════════════════════════════════════════════════════════════════════════
// Read-only modal listing inventory summary stat rows (category + location + optional detail)
// ═══════════════════════════════════════════════════════════════════════════════

import type { ReactElement, ReactNode } from 'react';
import { INVENTORY_LEVEL, type InventoryLevel } from '@fdh/shared';
import { AppModal } from '../ui/AppModal';
import { HomeStockLevelIcon } from './HomeStockLevelIcon';

export type InventorySummaryStatDetailRow = {
  rowKey: string;
  itemName: string;
  categoryName: string;
  locationName: string;
  stockLevel?: InventoryLevel;
  stockLevelLabel?: string;
  detailLine?: string;
};

type InventorySummaryStatDetailModalProps = {
  open: boolean;
  title: string;
  titleTrailing?: ReactNode;
  categoryLabel: string;
  locationLabel: string;
  emptyMessage: string;
  expiryHeading?: string;
  rows: InventorySummaryStatDetailRow[];
  closeAriaLabel: string;
  backdropDismissAriaLabel: string;
  onClose: () => void;
};

export const InventorySummaryStatDetailModal = ({
  open,
  title,
  titleTrailing,
  categoryLabel,
  locationLabel,
  emptyMessage,
  expiryHeading,
  rows,
  closeAriaLabel,
  onClose,
}: InventorySummaryStatDetailModalProps): ReactElement => (
  <AppModal
    open={open}
    onClose={onClose}
    title={title}
    headerEnd={titleTrailing}
    closeAriaLabel={closeAriaLabel}
    backdropZClass="z-[120]"
  >
    {rows.length === 0 ? (
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300" role="status">
        {emptyMessage}
      </p>
    ) : (
      <ul className="mt-1 list-none space-y-3 p-0" role="list">
        {rows.map((row) => (
          <li
            key={row.rowKey}
            className="rounded-xl border border-gray-200/90 bg-gray-50/90 p-3 dark:border-gray-600 dark:bg-gray-800/60"
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold leading-snug text-gray-900 dark:text-gray-50">
                  {row.itemName}
                </p>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                  <span className="font-semibold text-gray-700 dark:text-gray-200">{categoryLabel}</span>
                  <span aria-hidden>: </span>
                  {row.categoryName || '—'}
                </p>
                <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">
                  <span className="font-semibold text-gray-700 dark:text-gray-200">{locationLabel}</span>
                  <span aria-hidden>: </span>
                  {row.locationName || '—'}
                </p>
                {row.detailLine && expiryHeading ? (
                  <p className="mt-2 border-t border-gray-200/80 pt-2 text-xs leading-relaxed text-gray-600 dark:border-gray-600 dark:text-gray-300">
                    <span className="font-semibold text-gray-700 dark:text-gray-200">{expiryHeading}</span>
                    <span aria-hidden>: </span>
                    {row.detailLine}
                  </p>
                ) : null}
              </div>
              {row.stockLevel && row.stockLevelLabel ? (
                <div
                  className="flex shrink-0 flex-col items-center gap-1 self-center"
                  aria-label={row.stockLevelLabel}
                >
                  <HomeStockLevelIcon
                    level={row.stockLevel}
                    size="md"
                    ariaLabel={row.stockLevelLabel}
                    blinkWhenEmpty={row.stockLevel === INVENTORY_LEVEL.EMPTY}
                  />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                    {row.stockLevelLabel}
                  </span>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    )}
  </AppModal>
);
